export const COPILOT_IMAGE_DRAG_MIME = 'application/x-streamlain-copilot-image';

export interface CopilotImageDragPayload {
  src: string;
  fileName: string;
}

type DragDataWriter = Pick<DataTransfer, 'setData'> & {
  effectAllowed?: DataTransfer['effectAllowed'];
};

type DragDataReader = Pick<DataTransfer, 'getData'>;

type DragDataTypeReader = Pick<DataTransfer, 'types'>;

export function createCopilotImageDragPayload(
  src: string,
  index = 0
): CopilotImageDragPayload {
  return {
    src,
    fileName: inferImageFileName(src, index),
  };
}

export function writeCopilotImageDragData(
  dataTransfer: DragDataWriter,
  src: string,
  index = 0
): CopilotImageDragPayload {
  const payload = createCopilotImageDragPayload(src, index);
  const serialized = JSON.stringify(payload);

  dataTransfer.effectAllowed = 'copy';
  dataTransfer.setData(COPILOT_IMAGE_DRAG_MIME, serialized);
  dataTransfer.setData('text/uri-list', src);
  dataTransfer.setData('text/plain', src);

  return payload;
}

export function hasCopilotImageDragData(
  dataTransfer: DragDataTypeReader | null | undefined
): boolean {
  return Array.from(dataTransfer?.types ?? []).includes(
    COPILOT_IMAGE_DRAG_MIME
  );
}

export function parseCopilotImageDragPayload(
  dataTransfer: DragDataReader | null | undefined
): CopilotImageDragPayload | null {
  const raw = dataTransfer?.getData(COPILOT_IMAGE_DRAG_MIME);
  if (!raw) return null;

  try {
    const value = JSON.parse(raw) as Partial<CopilotImageDragPayload>;
    if (typeof value.src !== 'string' || !value.src.trim()) return null;
    const inferredFileName = inferImageFileName(value.src);
    const fileName =
      typeof value.fileName === 'string' && value.fileName.trim()
        ? sanitizeFileName(value.fileName) || inferredFileName
        : inferredFileName;

    return {
      src: value.src,
      fileName,
    };
  } catch {
    return null;
  }
}

function inferImageFileName(src: string, index = 0): string {
  const fallback = `copilot-image-${index + 1}.png`;
  if (!src || src.startsWith('data:') || src.startsWith('blob:')) {
    return fallback;
  }

  try {
    const base =
      typeof window === 'undefined'
        ? 'http://localhost/'
        : window.location.href;
    const url = new URL(src, base);
    const lastSegment = decodeURIComponent(
      url.pathname.split('/').filter(Boolean).at(-1) ?? ''
    );
    const fileName = sanitizeFileName(lastSegment);
    return /\.[a-z0-9]{2,8}$/i.test(fileName) ? fileName : fallback;
  } catch {
    return fallback;
  }
}

function sanitizeFileName(value: string): string {
  return value
    .trim()
    .replace(/[^\w.-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
